import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaFilter,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaReceipt,
  FaChartBar,
  FaChartLine,
  FaChartPie,
  FaDownload,
  FaSync,
  FaCreditCard,
  FaFileInvoiceDollar,
  FaPercentage,
  FaCalendarDay,
  FaDatabase,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Import the CSS file
import "./SoloPaymentHistoryStatistics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PaymentHistoryStatistics = ({
  studentData,
  isFamilyStudent,
  familyData,
}) => {
  const [activeTab, setActiveTab] = useState("history");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Database Connection State
  const [isDbConnected, setIsDbConnected] = useState(true);
  const [dbConnectionError, setDbConnectionError] = useState("");
  const [isCheckingDb, setIsCheckingDb] = useState(false);

  // Payment History State
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Statistics State
  const [statsData, setStatsData] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [chartType, setChartType] = useState("line");
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());

  // Error states
  const [paymentHistoryError, setPaymentHistoryError] = useState("");
  const [statisticsError, setStatisticsError] = useState("");

  // Check database connection
  const checkDatabaseConnection = async () => {
    setIsCheckingDb(true);
    setDbConnectionError("");

    try {
      const response = await axios.get(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php",
        {
          timeout: 5000,
          params: {
            action: "test-student-fee-status",
            student_id: studentData?.studentId || "",
          },
        }
      );

      console.log("Database connection test response:", response.data);

      if (response.data.success || response.data.test_status === "OK") {
        setIsDbConnected(true);
        return true;
      } else {
        setIsDbConnected(false);
        setDbConnectionError(
          response.data.message || "Database connection failed"
        );
        return false;
      }
    } catch (error) {
      console.error("Database connection test failed:", error);
      setIsDbConnected(false);

      // Try alternative test endpoint
      try {
        const altResponse = await axios.get(
          "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?debug-fee-tables",
          { timeout: 3000 }
        );

        if (altResponse.data.debug_info) {
          setIsDbConnected(true);
          return true;
        }
      } catch (altError) {
        console.log("Alternative test also failed");
      }

      if (
        error.code === "ERR_NETWORK" ||
        error.message.includes("Network Error")
      ) {
        setDbConnectionError(
          "Network error. Please check if XAMPP/WAMP is running with Apache."
        );
      } else if (error.code === "ECONNABORTED") {
        setDbConnectionError(
          "Connection timeout. Server might be offline or busy."
        );
      } else if (error.response?.status === 400) {
        if (error.response.data?.available_actions) {
          setDbConnectionError(
            `API is reachable but test action not found. Available actions: ${error.response.data.available_actions
              .slice(0, 3)
              .join(", ")}...`
          );
          setIsDbConnected(true);
          return true;
        } else {
          setDbConnectionError("Bad request to API. Check action parameter.");
        }
      } else if (error.response?.status === 404) {
        setDbConnectionError(
          "API endpoint not found (404). Check the file path."
        );
      } else if (error.response) {
        setDbConnectionError(
          `Server error: ${error.response.status} - ${error.response.statusText}`
        );
      } else if (error.request) {
        setDbConnectionError(
          "No response from server. Check if Apache is running in XAMPP."
        );
      } else {
        setDbConnectionError("Connection failed: " + error.message);
      }

      return false;
    } finally {
      setIsCheckingDb(false);
    }
  };

  // Fetch Payment History with error handling
  const fetchPaymentHistory = async (page = 1) => {
    if (
      (!studentData?.studentId && !isFamilyStudent) ||
      (isFamilyStudent && !familyData?.familyCode)
    ) {
      console.log("Missing student data or family data");
      return;
    }

    setIsLoading(true);
    setPaymentHistoryError("");

    try {
      const payload = {
        filter: filter,
        page: page,
        limit: pagination.limit,
        start_date: dateRange.startDate || undefined,
        end_date: dateRange.endDate || undefined,
      };

      if (!isFamilyStudent && studentData?.studentId) {
        payload.student_id = studentData.studentId;
      } else if (isFamilyStudent && familyData?.familyCode) {
        payload.family_code = familyData.familyCode;
      }

      console.log("Fetching payment history with payload:", payload);

      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-payment-history",
        payload,
        {
          timeout: 15000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Payment history response:", response.data);

      if (response.data.success) {
        setPaymentHistory(response.data.data || []);
        setPagination(
          response.data.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
          }
        );
        setIsDbConnected(true);
        setPaymentHistoryError("");
      } else {
        setPaymentHistoryError(
          response.data.error || "Failed to load payment history"
        );
        setPaymentHistory([]);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);

      let errorMessage = "Failed to fetch payment history";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setPaymentHistoryError(errorMessage);
      setPaymentHistory([]);

      if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") {
        setIsDbConnected(false);
        setDbConnectionError("Request timeout. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Statistics with error handling
  const fetchStatistics = async () => {
    if (
      (!studentData?.studentId && !isFamilyStudent) ||
      (isFamilyStudent && !familyData?.familyCode)
    ) {
      return;
    }

    setIsLoadingStats(true);
    setStatisticsError("");

    try {
      const payload = {
        year: statsYear,
      };

      if (!isFamilyStudent && studentData?.studentId) {
        payload.student_id = studentData.studentId;
      } else if (isFamilyStudent && familyData?.familyCode) {
        payload.family_code = familyData.familyCode;
      }

      console.log("Fetching statistics with payload:", payload);

      // Fetch detailed statistics
      const statsResponse = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-statistics-data",
        payload,
        {
          timeout: 15000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Statistics response:", statsResponse.data);

      if (statsResponse.data.success) {
        setStatsData(statsResponse.data);
        setStatisticsError("");
      } else {
        setStatisticsError(
          statsResponse.data.error || "Failed to load statistics"
        );
        setStatsData(null);
      }

      // Fetch quick stats
      try {
        const quickStatsResponse = await axios.post(
          "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php?action=get-quick-stats",
          payload,
          {
            timeout: 10000,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (quickStatsResponse.data.success) {
          setQuickStats(quickStatsResponse.data.stats);
        }
      } catch (quickError) {
        console.warn("Quick stats fetch failed:", quickError);
      }

      setIsDbConnected(true);
    } catch (error) {
      console.error("Error fetching statistics:", error);

      let errorMessage = "Failed to fetch statistics";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setStatisticsError(errorMessage);
      setStatsData(null);
      setQuickStats(null);

      if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") {
        setIsDbConnected(false);
        setDbConnectionError("Request timeout. Please try again.");
      }
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Format currency in Pakistani Rupees
  const formatCurrency = (amount) => {
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format date in Pakistani format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-PK", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!statsData?.chart_data) {
      // Return empty/default chart data
      return {
        labels: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        datasets: [
          {
            label: "Fee Payments",
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgb(75, 192, 192)",
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            tension: 0.3,
            fill: true,
          },
          {
            label: "Expense Payments",
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgb(255, 99, 132)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            tension: 0.3,
            fill: true,
          },
        ],
      };
    }

    const labels = statsData.chart_data.map((item) => item.month_name);
    const feeData = statsData.chart_data.map((item) => item.fee_paid || 0);
    const expenseData = statsData.chart_data.map(
      (item) => item.expense_paid || 0
    );

    if (chartType === "line") {
      return {
        labels,
        datasets: [
          {
            label: "Fee Payments",
            data: feeData,
            borderColor: "rgb(75, 192, 192)",
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            tension: 0.3,
            fill: true,
          },
          {
            label: "Expense Payments",
            data: expenseData,
            borderColor: "rgb(255, 99, 132)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            tension: 0.3,
            fill: true,
          },
        ],
      };
    } else if (chartType === "bar") {
      return {
        labels,
        datasets: [
          {
            label: "Fee Paid",
            data: feeData,
            backgroundColor: "rgba(54, 162, 235, 0.7)",
            borderColor: "rgb(54, 162, 235)",
            borderWidth: 1,
          },
          {
            label: "Expense Paid",
            data: expenseData,
            backgroundColor: "rgba(255, 99, 132, 0.7)",
            borderColor: "rgb(255, 99, 132)",
            borderWidth: 1,
          },
          {
            label: "Fee Pending",
            data: statsData.chart_data.map((item) => item.fee_pending || 0),
            backgroundColor: "rgba(255, 159, 64, 0.7)",
            borderColor: "rgb(255, 159, 64)",
            borderWidth: 1,
          },
          {
            label: "Expense Pending",
            data: statsData.chart_data.map((item) => item.expense_pending || 0),
            backgroundColor: "rgba(153, 102, 255, 0.7)",
            borderColor: "rgb(153, 102, 255)",
            borderWidth: 1,
          },
        ],
      };
    } else if (chartType === "pie") {
      const totalFee = statsData.summary?.total_fee || 0;
      const totalExpense = statsData.summary?.total_expense || 0;

      return {
        labels: ["Total Fee", "Total Expense"],
        datasets: [
          {
            data: [totalFee, totalExpense],
            backgroundColor: [
              "rgba(54, 162, 235, 0.7)",
              "rgba(255, 99, 132, 0.7)",
            ],
            borderColor: ["rgb(54, 162, 235)", "rgb(255, 99, 132)"],
            borderWidth: 1,
          },
        ],
      };
    }

    return null;
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Payment Statistics - ${statsYear}`,
        font: {
          size: 16,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          },
        },
      },
    },
    scales:
      chartType !== "pie"
        ? {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function (value) {
                  return formatCurrency(value);
                },
              },
            },
          }
        : undefined,
  };

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // Reset filters
  const resetFilters = () => {
    setFilter("all");
    setDateRange({
      startDate: "",
      endDate: "",
    });
  };

  // Export data
  const exportToCSV = () => {
    if (paymentHistory.length === 0) {
      alert("No data to export!");
      return;
    }

    let csvContent = "data:text/csv;charset=utf-8,";

    // Add headers
    csvContent +=
      "Date,Type,Description,Amount,Paid,Pending,Payment Mode,Transaction ID,Status\n";

    // Add rows
    paymentHistory.forEach((record) => {
      const pending = parseFloat(record.pending_amount || 0);
      const paid = parseFloat(record.paid_amount || 0);
      let status = "Unpaid";
      if (pending === 0) status = "Paid";
      else if (paid > 0) status = "Partial";

      const row = [
        formatDate(record.created_at),
        record.type_display || record.type,
        record.description || "",
        formatCurrency(record.amount),
        formatCurrency(record.paid_amount),
        formatCurrency(record.pending_amount),
        record.payment_mode || "N/A",
        record.transaction_id || "N/A",
        status,
      ].join(",");
      csvContent += row + "\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `payment_history_${new Date().getTime()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Initial fetch
  useEffect(() => {
    checkDatabaseConnection();

    if (activeTab === "history") {
      fetchPaymentHistory();
    } else if (activeTab === "statistics") {
      fetchStatistics();
    }
  }, [activeTab, filter, dateRange.startDate, dateRange.endDate, statsYear]);

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchPaymentHistory(newPage);
  };

  // Get status badge
  const getStatusBadge = (record) => {
    const pending = parseFloat(record.pending_amount || 0);
    const paid = parseFloat(record.paid_amount || 0);

    if (pending === 0) {
      return <span className="status-badge status-paid">Paid</span>;
    } else if (paid > 0) {
      return <span className="status-badge status-partial">Partial</span>;
    } else {
      return <span className="status-badge status-unpaid">Unpaid</span>;
    }
  };

  // Get type icon
  const getTypeIcon = (type) => {
    if (type === "monthly_fee" || type === "fee") {
      return <FaCreditCard className="type-icon fee-icon" />;
    } else {
      return <FaReceipt className="type-icon expense-icon" />;
    }
  };

  return (
    <div className="payment-history-stats-container">
      {/* Database Connection Status */}
      <div
        className={`database-status ${
          isDbConnected ? "connected" : "disconnected"
        }`}
      >
        <div className="database-status-content">
          {isDbConnected ? (
            <>
              <FaCheckCircle className="database-status-icon" />
              <span className="database-status-text">Database Connected</span>
            </>
          ) : (
            <>
              <FaExclamationTriangle className="database-status-icon" />
              <span className="database-status-text">
                Database Connection Issue:{" "}
                {dbConnectionError || "Unable to connect"}
              </span>
            </>
          )}
          <button
            className="database-refresh-btn"
            onClick={checkDatabaseConnection}
            disabled={isCheckingDb}
          >
            {isCheckingDb ? <FaSync className="spin" /> : "Retry"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="phs-tabs">
        <button
          className={`phs-tab ${
            activeTab === "history" ? "phs-tab-active" : ""
          }`}
          onClick={() => setActiveTab("history")}
        >
          <FaFileInvoiceDollar className="phs-tab-icon" />
          Payment History
        </button>
        <button
          className={`phs-tab ${
            activeTab === "statistics" ? "phs-tab-active" : ""
          }`}
          onClick={() => setActiveTab("statistics")}
        >
          <FaChartLine className="phs-tab-icon" />
          Statistics & Graphs
        </button>
      </div>

      {/* Payment History Tab */}
      {activeTab === "history" && (
        <div className="phs-history-tab">
          {/* Header */}
          <div className="phs-header">
            <h3>
              <FaFileInvoiceDollar className="phs-header-icon" />
              Payment History
              <span className="phs-subtitle">
                {isFamilyStudent
                  ? `Family: ${familyData?.familyCode || "N/A"}`
                  : `Student: ${studentData?.studentName || "N/A"}`}
              </span>
            </h3>

            <div className="phs-actions">
              <button
                className="phs-export-btn"
                onClick={exportToCSV}
                disabled={paymentHistory.length === 0 || !isDbConnected}
              >
                <FaDownload className="phs-export-icon" />
                Export CSV
              </button>
              <button
                className="phs-refresh-btn"
                onClick={() => fetchPaymentHistory()}
                disabled={isLoading || !isDbConnected}
              >
                <FaSync
                  className={`phs-refresh-icon ${isLoading ? "spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="phs-filters">
            <div className="phs-filter-group">
              <label className="phs-filter-label">
                <FaFilter className="phs-filter-icon" />
                Filter by Type
              </label>
              <div className="phs-filter-buttons">
                <button
                  className={`phs-filter-btn ${
                    filter === "all" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("all")}
                  disabled={!isDbConnected}
                >
                  All Transactions
                </button>
                <button
                  className={`phs-filter-btn ${
                    filter === "fee" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("fee")}
                  disabled={!isDbConnected}
                >
                  <FaCreditCard className="phs-filter-btn-icon" />
                  Fee Only
                </button>
                <button
                  className={`phs-filter-btn ${
                    filter === "expense" ? "active" : ""
                  }`}
                  onClick={() => handleFilterChange("expense")}
                  disabled={!isDbConnected}
                >
                  <FaReceipt className="phs-filter-btn-icon" />
                  Expense Only
                </button>
              </div>
            </div>

            <div className="phs-filter-group">
              <label className="phs-filter-label">
                <FaCalendarAlt className="phs-filter-icon" />
                Date Range
              </label>
              <div className="phs-date-range">
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateRangeChange}
                  className="phs-date-input"
                  placeholder="From Date"
                  disabled={!isDbConnected}
                />
                <span className="phs-date-separator">to</span>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateRangeChange}
                  className="phs-date-input"
                  placeholder="To Date"
                  disabled={!isDbConnected}
                />
                <button
                  className="phs-reset-btn"
                  onClick={resetFilters}
                  disabled={!isDbConnected}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Database Warning */}
          {!isDbConnected && (
            <div className="phs-empty-state">
              <FaExclamationTriangle className="phs-empty-icon" />
              <h4>Database Connection Required</h4>
              <p>
                {dbConnectionError ||
                  "Please check your database connection and try again."}
              </p>
              <button
                className="phs-empty-action"
                onClick={checkDatabaseConnection}
              >
                <FaSync className={isCheckingDb ? "spin" : ""} />
                {isCheckingDb ? "Checking..." : "Retry Connection"}
              </button>
            </div>
          )}

          {/* Summary Stats - Only show if connected */}
          {isDbConnected && paymentHistory.length > 0 && (
            <div className="phs-summary-cards">
              <div className="phs-summary-card">
                <div className="phs-summary-card-header">
                  <FaMoneyBillWave className="phs-summary-card-icon fee" />
                  <span>Fee Transactions</span>
                </div>
                <div className="phs-summary-card-content">
                  <div className="phs-summary-card-value">
                    {
                      paymentHistory.filter(
                        (r) => r.type === "fee" || r.type === "monthly_fee"
                      ).length
                    }
                  </div>
                  <div className="phs-summary-card-subtitle">
                    Total Fee:{" "}
                    {formatCurrency(
                      paymentHistory
                        .filter(
                          (r) => r.type === "fee" || r.type === "monthly_fee"
                        )
                        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
                    )}
                  </div>
                </div>
              </div>

              <div className="phs-summary-card">
                <div className="phs-summary-card-header">
                  <FaReceipt className="phs-summary-card-icon expense" />
                  <span>Expense Transactions</span>
                </div>
                <div className="phs-summary-card-content">
                  <div className="phs-summary-card-value">
                    {paymentHistory.filter((r) => r.type === "expense").length}
                  </div>
                  <div className="phs-summary-card-subtitle">
                    Total Expense:{" "}
                    {formatCurrency(
                      paymentHistory
                        .filter((r) => r.type === "expense")
                        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)
                    )}
                  </div>
                </div>
              </div>

              <div className="phs-summary-card">
                <div className="phs-summary-card-header">
                  <FaDatabase className="phs-summary-card-icon total" />
                  <span>Totals</span>
                </div>
                <div className="phs-summary-card-content">
                  <div className="phs-summary-card-value">
                    {paymentHistory.length}
                  </div>
                  <div className="phs-summary-card-subtitle">
                    Amount Paid:{" "}
                    {formatCurrency(
                      paymentHistory.reduce(
                        (sum, r) => sum + parseFloat(r.paid_amount || 0),
                        0
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Table */}
          <div className="phs-table-container">
            {isLoading ? (
              <div className="phs-loading">
                <div className="phs-spinner"></div>
                <p>Loading payment history...</p>
              </div>
            ) : !isDbConnected ? (
              <div className="phs-empty-state">
                <FaDatabase className="phs-empty-icon" />
                <h4>Database Not Connected</h4>
                <p>
                  Please check your database connection and server
                  configuration.
                </p>
              </div>
            ) : paymentHistoryError ? (
              <div className="phs-empty-state">
                <FaExclamationTriangle className="phs-empty-icon" />
                <h4>Error Loading Payment History</h4>
                <p>{paymentHistoryError}</p>
                <button
                  className="phs-empty-action"
                  onClick={() => fetchPaymentHistory()}
                >
                  <FaSync className={isLoading ? "spin" : ""} />
                  Retry
                </button>
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="phs-empty-state">
                <FaFileInvoiceDollar className="phs-empty-icon" />
                <h4>No Transactions Found</h4>
                <p>No payment history found for the selected filters.</p>
                <button className="phs-empty-action" onClick={resetFilters}>
                  Reset Filters
                </button>
              </div>
            ) : (
              <>
                <table className="phs-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Pending</th>
                      <th>Status</th>
                      <th>Payment Mode</th>
                      <th>Transaction ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((record, index) => (
                      <tr key={index}>
                        <td className="phs-date-cell">
                          {formatDate(record.created_at)}
                        </td>
                        <td className="phs-type-cell">
                          <div className="phs-type-wrapper">
                            {getTypeIcon(record.type)}
                            <span className={`phs-type-text ${record.type}`}>
                              {record.type_display || record.type}
                            </span>
                          </div>
                        </td>
                        <td className="phs-description-cell">
                          {record.description || "No description"}
                          {record.remarks && (
                            <div className="phs-remarks">{record.remarks}</div>
                          )}
                        </td>
                        <td className="phs-amount-cell">
                          <strong>{formatCurrency(record.amount)}</strong>
                        </td>
                        <td className="phs-paid-cell">
                          <span className="phs-paid-amount">
                            {formatCurrency(record.paid_amount)}
                          </span>
                        </td>
                        <td className="phs-pending-cell">
                          <span
                            className={`phs-pending-amount ${
                              parseFloat(record.pending_amount || 0) === 0
                                ? "zero"
                                : ""
                            }`}
                          >
                            {formatCurrency(record.pending_amount)}
                          </span>
                        </td>
                        <td className="phs-status-cell">
                          {getStatusBadge(record)}
                        </td>
                        <td className="phs-mode-cell">
                          <span
                            className={`phs-mode-badge ${record.payment_mode}`}
                          >
                            {record.payment_mode || "N/A"}
                          </span>
                        </td>
                        <td className="phs-transaction-cell">
                          {record.transaction_id || "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="phs-pagination">
                    <button
                      className="phs-pagination-btn"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1 || !isDbConnected}
                    >
                      Previous
                    </button>
                    <span className="phs-pagination-info">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      className="phs-pagination-btn"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={
                        pagination.page >= pagination.totalPages ||
                        !isDbConnected
                      }
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === "statistics" && (
        <div className="phs-statistics-tab">
          {/* Header */}
          <div className="phs-header">
            <h3>
              <FaChartLine className="phs-header-icon" />
              Statistics & Graphs
              <span className="phs-subtitle">
                {isFamilyStudent
                  ? `Family: ${familyData?.familyCode || "N/A"}`
                  : `Student: ${studentData?.studentName || "N/A"}`}
              </span>
            </h3>

            <div className="phs-statistics-controls">
              <div className="phs-year-selector">
                <label>Year:</label>
                <select
                  value={statsYear}
                  onChange={(e) => setStatsYear(parseInt(e.target.value))}
                  className="phs-year-select"
                  disabled={!isDbConnected}
                >
                  {[2022, 2023, 2024, 2025, 2026].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className="phs-chart-type-selector">
                <button
                  className={`phs-chart-type-btn ${
                    chartType === "line" ? "active" : ""
                  }`}
                  onClick={() => setChartType("line")}
                  disabled={!isDbConnected}
                >
                  <FaChartLine className="phs-chart-type-icon" />
                  Line
                </button>
                <button
                  className={`phs-chart-type-btn ${
                    chartType === "bar" ? "active" : ""
                  }`}
                  onClick={() => setChartType("bar")}
                  disabled={!isDbConnected}
                >
                  <FaChartBar className="phs-chart-type-icon" />
                  Bar
                </button>
                <button
                  className={`phs-chart-type-btn ${
                    chartType === "pie" ? "active" : ""
                  }`}
                  onClick={() => setChartType("pie")}
                  disabled={!isDbConnected}
                >
                  <FaChartPie className="phs-chart-type-icon" />
                  Pie
                </button>
              </div>

              <button
                className="phs-refresh-btn"
                onClick={fetchStatistics}
                disabled={isLoadingStats || !isDbConnected}
              >
                <FaSync
                  className={`phs-refresh-icon ${isLoadingStats ? "spin" : ""}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {/* Database Warning for Statistics */}
          {!isDbConnected && (
            <div className="phs-empty-state">
              <FaExclamationTriangle className="phs-empty-icon" />
              <h4>Database Connection Required</h4>
              <p>
                Statistics data cannot be loaded without a database connection.
              </p>
              <button
                className="phs-empty-action"
                onClick={checkDatabaseConnection}
              >
                <FaSync className={isCheckingDb ? "spin" : ""} />
                {isCheckingDb ? "Checking..." : "Retry Connection"}
              </button>
            </div>
          )}

          {/* Quick Stats - Only show if connected */}
          {isDbConnected && quickStats && (
            <div className="phs-quick-stats">
              <div className="phs-quick-stat-card current-month">
                <div className="phs-quick-stat-header">
                  <FaCalendarDay className="phs-quick-stat-icon" />
                  <h4>
                    Current Month ({quickStats.current_month?.month || "N/A"})
                  </h4>
                </div>
                <div className="phs-quick-stat-content">
                  <div className="phs-quick-stat-row">
                    <span>Fee:</span>
                    <strong>
                      {formatCurrency(
                        quickStats.current_month?.fee?.total || 0
                      )}
                    </strong>
                    <span className="phs-quick-stat-sub">
                      Paid:{" "}
                      {formatCurrency(quickStats.current_month?.fee?.paid || 0)}
                    </span>
                  </div>
                  <div className="phs-quick-stat-row">
                    <span>Expense:</span>
                    <strong>
                      {formatCurrency(
                        quickStats.current_month?.expense?.total || 0
                      )}
                    </strong>
                    <span className="phs-quick-stat-sub">
                      Paid:{" "}
                      {formatCurrency(
                        quickStats.current_month?.expense?.paid || 0
                      )}
                    </span>
                  </div>
                  <div className="phs-quick-stat-row total">
                    <span>Total:</span>
                    <strong>
                      {formatCurrency(
                        quickStats.current_month?.totals?.total_amount || 0
                      )}
                    </strong>
                    <span className="phs-quick-stat-sub">
                      Transactions:{" "}
                      {quickStats.current_month?.totals?.total_transactions ||
                        0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="phs-quick-stat-card year-to-date">
                <div className="phs-quick-stat-header">
                  <FaCalendarAlt className="phs-quick-stat-icon" />
                  <h4>Year to Date ({statsYear})</h4>
                </div>
                <div className="phs-quick-stat-content">
                  <div className="phs-quick-stat-row">
                    <span>Fee:</span>
                    <strong>
                      {formatCurrency(quickStats.year_to_date?.fee?.total || 0)}
                    </strong>
                    <span className="phs-quick-stat-sub">
                      Transactions:{" "}
                      {quickStats.year_to_date?.fee?.transactions || 0}
                    </span>
                  </div>
                  <div className="phs-quick-stat-row">
                    <span>Expense:</span>
                    <strong>
                      {formatCurrency(
                        quickStats.year_to_date?.expense?.total || 0
                      )}
                    </strong>
                    <span className="phs-quick-stat-sub">
                      Transactions:{" "}
                      {quickStats.year_to_date?.expense?.transactions || 0}
                    </span>
                  </div>
                  <div className="phs-quick-stat-row total">
                    <span>Total Paid:</span>
                    <strong>
                      {formatCurrency(
                        (quickStats.year_to_date?.fee?.paid || 0) +
                          (quickStats.year_to_date?.expense?.paid || 0)
                      )}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="phs-quick-stat-card overall">
                <div className="phs-quick-stat-header">
                  <FaChartLine className="phs-quick-stat-icon" />
                  <h4>Overall Summary</h4>
                </div>
                <div className="phs-quick-stat-content">
                  <div className="phs-quick-stat-row">
                    <span>Total Transactions:</span>
                    <strong>
                      {quickStats.totals?.total_transactions || 0}
                    </strong>
                  </div>
                  <div className="phs-quick-stat-row">
                    <span>Total Amount:</span>
                    <strong>
                      {formatCurrency(quickStats.totals?.total_amount || 0)}
                    </strong>
                  </div>
                  <div className="phs-quick-stat-row">
                    <span>Total Paid:</span>
                    <strong className="paid">
                      {formatCurrency(quickStats.totals?.total_paid || 0)}
                    </strong>
                  </div>
                  <div className="phs-quick-stat-row">
                    <span>Total Pending:</span>
                    <strong className="pending">
                      {formatCurrency(quickStats.totals?.total_pending || 0)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="phs-charts-container">
            {isLoadingStats ? (
              <div className="phs-loading">
                <div className="phs-spinner"></div>
                <p>Loading statistics data...</p>
              </div>
            ) : !isDbConnected ? (
              <div className="phs-empty-state">
                <FaChartLine className="phs-empty-icon" />
                <h4>No Database Connection</h4>
                <p>Please connect to database to view statistics.</p>
              </div>
            ) : statisticsError ? (
              <div className="phs-empty-state">
                <FaExclamationTriangle className="phs-empty-icon" />
                <h4>Error Loading Statistics</h4>
                <p>{statisticsError}</p>
                <button
                  className="phs-empty-action"
                  onClick={() => fetchStatistics()}
                >
                  <FaSync className={isLoadingStats ? "spin" : ""} />
                  Retry
                </button>
              </div>
            ) : statsData ? (
              <>
                <div className="phs-chart-wrapper">
                  {chartType === "line" && prepareChartData() && (
                    <Line data={prepareChartData()} options={chartOptions} />
                  )}
                  {chartType === "bar" && prepareChartData() && (
                    <Bar data={prepareChartData()} options={chartOptions} />
                  )}
                  {chartType === "pie" && prepareChartData() && (
                    <Pie data={prepareChartData()} options={chartOptions} />
                  )}
                </div>

                {/* Detailed Statistics */}
                {statsData.summary && (
                  <div className="phs-detailed-stats">
                    <h4>Detailed Statistics</h4>
                    <div className="phs-detailed-stats-grid">
                      <div className="phs-detailed-stat">
                        <div className="phs-detailed-stat-label">
                          <FaMoneyBillWave className="phs-detailed-stat-icon" />
                          Total Fee
                        </div>
                        <div className="phs-detailed-stat-value">
                          {formatCurrency(statsData.summary.total_fee || 0)}
                        </div>
                        <div className="phs-detailed-stat-sub">
                          {statsData.summary.fee_transactions || 0} transactions
                        </div>
                      </div>

                      <div className="phs-detailed-stat">
                        <div className="phs-detailed-stat-label">
                          <FaMoneyBillWave className="phs-detailed-stat-icon" />
                          Total Expense
                        </div>
                        <div className="phs-detailed-stat-value">
                          {formatCurrency(statsData.summary.total_expense || 0)}
                        </div>
                        <div className="phs-detailed-stat-sub">
                          {statsData.summary.expense_transactions || 0}{" "}
                          transactions
                        </div>
                      </div>

                      <div className="phs-detailed-stat">
                        <div className="phs-detailed-stat-label">
                          <FaPercentage className="phs-detailed-stat-icon" />
                          Fee Percentage
                        </div>
                        <div className="phs-detailed-stat-value">
                          {statsData.summary.fee_percentage || 0}%
                        </div>
                      </div>

                      <div className="phs-detailed-stat">
                        <div className="phs-detailed-stat-label">
                          <FaPercentage className="phs-detailed-stat-icon" />
                          Expense Percentage
                        </div>
                        <div className="phs-detailed-stat-value">
                          {statsData.summary.expense_percentage || 0}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="phs-empty-state">
                <FaChartLine className="phs-empty-icon" />
                <h4>No Statistics Data</h4>
                <p>No statistics data available for the selected filters.</p>
                <button
                  className="phs-empty-action"
                  onClick={() => fetchStatistics()}
                >
                  <FaSync className={isLoadingStats ? "spin" : ""} />
                  Load Statistics
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryStatistics;
